import { useState } from "react";
import {
  Briefcase,
  CheckSquare,
  Megaphone,
  TrendingUp,
  Users,
  Palette,
  Code,
} from "lucide-react";
import ApplicationForm from "@/components/sections/careers/ApplicationForm";

const introText =
  "At Elite Eventz, we create memorable experiences through cultural festivals, concerts, sports events and brand activations. We are looking for creative, driven and enthusiastic individuals who want to contribute, learn and grow while working on real projects.";

// `name` is the domain value the application backend expects (matches the
// resume-required list and the Apps Script's domain whitelist exactly).
// `displayName` is the candidate-facing title on this page — kept distinct
// per the manager-provided copy (e.g. "Graphic Design" displays, but the
// application is recorded under the existing "Designing" domain).
const domains = [
  {
    id: "production",
    displayName: "Production",
    name: "Production Management",
    tagline: "Make the event come alive.",
    description:
      "Handle event budgets, vendor selection and production planning. Coordinate decor, lighting, photography, setups and other event requirements while ensuring everything is delivered on time and within budget.",
    youShouldBe:
      "Organised, resourceful, good at negotiation and comfortable working with different people.",
    icon: Briefcase,
  },
  {
    id: "marketing",
    displayName: "Marketing",
    name: "Marketing",
    tagline: "Make people want to be there.",
    description:
      "Plan social media and marketing campaigns, develop marketing strategies, manage Meta Ads, connect with potential customers and experiment with creative ways to grow event reach and engagement.",
    youShouldBe: "Creative, strategic, social-media savvy and full of ideas.",
    icon: TrendingUp,
  },
  {
    id: "designing",
    displayName: "Graphic Design",
    name: "Designing",
    tagline: "Turn ideas into visuals.",
    description:
      "Create posters, social media creatives, event branding and marketing collaterals. Work with Illustrator, Canva, CorelDRAW and 3D tools to create professional, high-quality designs.",
    youShouldBe: "Creative, fast, detail-oriented and passionate about design.",
    icon: Palette,
  },
  {
    id: "execution",
    displayName: "Event Execution",
    name: "Execution Management",
    tagline: "Bring the event to life.",
    description:
      "Work with vendors, teams and partners to coordinate event activities and ensure everything comes together smoothly. Be part of the on-ground experience and help create memorable events.",
    youShouldBe: "Organised, energetic, communicative and enthusiastic about events.",
    icon: CheckSquare,
  },
  {
    id: "pr",
    displayName: "Public Relations",
    name: "Public Relations",
    tagline: "Get people talking about us.",
    description:
      "Build relationships with media, influencers and communities. Plan offline PR campaigns, secure media coverage and amplify our marketing campaigns.",
    youShouldBe: "A confident communicator, relationship-builder and proactive networker.",
    icon: Megaphone,
  },
  {
    id: "sales",
    displayName: "Sales",
    name: "Sales",
    tagline: "Turn conversations into opportunities.",
    description:
      "Drive ticket sales and sponsorship sales. Identify leads, pitch opportunities, build relationships, negotiate and convert conversations into results.",
    youShouldBe: "Confident, persuasive, target-driven and persistent.",
    icon: Users,
  },
  {
    id: "sde",
    displayName: "Software Development & IT",
    name: "SDE & IT Support",
    tagline: "Build the technology behind the experiences.",
    description:
      "Develop in-house software, websites and internal tools. Work on UI, automation and digital systems while providing IT support and helping the team adopt better technology.",
    youShouldBe:
      "A problem solver with web development skills, technical curiosity and a passion for building useful things.",
    icon: Code,
  },
];

export default function DomainsSection() {
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const handleDomainSelect = (domain) => {
    setSelectedDomain(domain);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setTimeout(() => setSelectedDomain(null), 300);
  };

  return (
    <>
      <section id="domains" data-testid="domains-section" className="section">
        <div className="max-w-4xl mx-auto">
          <div className="fade-up text-center mb-16 md:mb-20">
            <div className="eyebrow mb-4 justify-center">Career Opportunities</div>
            <h2 className="section-title">
              Find Your Place at{" "}
              <span className="gold-text-gradient italic">Elite Eventz</span>
            </h2>
            <p className="mt-4 text-neutral-400">
              Explore the areas where you can contribute your skills, creativity
              and ideas.
            </p>
            <p className="mt-6 text-neutral-300 leading-relaxed max-w-3xl mx-auto">
              {introText}
            </p>
          </div>

          <div className="space-y-10 md:space-y-14">
            {domains.map((domain, index) => {
              const Icon = domain.icon;
              return (
                <div
                  key={domain.id}
                  data-testid={`domain-card-${domain.id}`}
                  className="fade-up group relative glass rounded-[1.75rem] border border-[var(--ee-border)] p-8 md:p-12 card-hover overflow-hidden"
                >
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none bg-gradient-to-br from-[var(--ee-gold)]/5 to-transparent" />

                  <span
                    aria-hidden
                    className="absolute -top-3 right-6 md:right-10 font-display text-[4.5rem] md:text-[6.5rem] leading-none text-white/[0.04] select-none"
                  >
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <div className="relative z-10">
                    <div className="flex items-center gap-4 mb-5">
                      <div className="w-12 h-12 rounded-xl border border-[var(--ee-border)] flex items-center justify-center shrink-0 group-hover:border-[var(--ee-gold)] transition-colors duration-500">
                        <Icon size={22} className="text-[var(--ee-gold)]" />
                      </div>
                      <h3 className="font-display text-2xl md:text-3xl font-semibold text-neutral-100">
                        {domain.displayName}
                      </h3>
                    </div>

                    <p className="gold-text-gradient italic font-display text-lg md:text-xl mb-5">
                      {domain.tagline}
                    </p>

                    <p className="text-neutral-300 leading-relaxed max-w-2xl">
                      {domain.description}
                    </p>

                    <div className="mt-6 bg-black/20 border border-[var(--ee-border)] rounded-xl p-4 md:p-5 max-w-2xl">
                      <p className="text-xs uppercase tracking-[0.22em] text-[var(--ee-gold)] mb-1.5">
                        You should be
                      </p>
                      <p className="text-sm md:text-base text-neutral-200 leading-relaxed">
                        {domain.youShouldBe}
                      </p>
                    </div>

                    <button
                      onClick={() => handleDomainSelect(domain)}
                      data-testid={`domain-apply-${domain.id}`}
                      className="btn-gold mt-8"
                    >
                      Apply Now{" "}
                      <span
                        aria-hidden
                        className="group-hover:translate-x-1 transition-transform duration-300"
                      >
                        →
                      </span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {showForm && selectedDomain && (
        <ApplicationForm domain={selectedDomain} onClose={handleCloseForm} />
      )}
    </>
  );
}
